/**
 * NativeSlider - schlanker Slider auf nativem CSS Scroll-Snap (ersetzt Embla Carousel).
 *
 * Wischen, Einrasten und Schwung macht der Browser selbst (Compositor, kein JS pro
 * Frame). Das Modul bietet genau den Teil der Embla-API, den die Sections nutzen:
 *
 *   const slider = NativeSlider(viewportNode, { loop, align }, [NativeSliderAutoplay({ delay })]);
 *   align: 'start' (Standard) oder 'center' (Slides rasten mittig ein)
 *   slider.scrollPrev() / scrollNext() / scrollTo(index, jump)
 *   slider.canScrollPrev() / canScrollNext()
 *   slider.scrollSnapList() / selectedScrollSnap()
 *   slider.on('init' | 'reInit' | 'select' | 'scroll' | 'settle', fn)
 *   slider.reInit() / destroy()
 *
 * Struktur wie bei Embla: viewport > container > slides. Die Scroll-Snap-Regeln
 * stehen in custom.css (.ns-viewport).
 *
 * Pfeile, Punkte, Autoplay und Maus-Drag gleiten per animateTo() mit weichem
 * Abbremsen ans Ziel (Gefuehl wie Embla); waehrenddessen ist das Einrasten aus
 * (.is-animating), damit der Browser nicht dazwischen springt. Touch scrollt nativ.
 */
(function () {
  'use strict';

  const DRAG_THRESHOLD = 30;
  // Wie weit der Schwung beim Loslassen nach einem Maus-Drag traegt (ms * px/ms)
  const MOMENTUM = 220;

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  /**
   * Gleitet ein Scroll-Element zu left (px). Dauer waechst mit der Strecke, bleibt
   * aber zwischen 320 und 700 ms. Bricht ab, sobald der Nutzer selbst eingreift.
   */
  function animateTo(el, left, duration) {
    if (el._nsAnim) el._nsAnim.cancel();
    const from = el.scrollLeft;
    const distance = left - from;
    if (Math.abs(distance) < 1) {
      el.scrollTo({ left, behavior: 'instant' });
      return Promise.resolve();
    }
    duration = duration || Math.min(700, Math.max(320, 260 + Math.abs(distance) * 0.35));
    el.classList.add('is-animating');

    return new Promise((resolve) => {
      let frame;
      const start = performance.now();
      const stopEvents = ['pointerdown', 'wheel', 'touchstart'];
      const finish = (jumpToEnd) => {
        cancelAnimationFrame(frame);
        stopEvents.forEach((ev) => el.removeEventListener(ev, onUser));
        if (jumpToEnd) el.scrollTo({ left, behavior: 'instant' });
        el.classList.remove('is-animating');
        el._nsAnim = null;
        resolve();
      };
      const onUser = () => finish(false);
      stopEvents.forEach((ev) => el.addEventListener(ev, onUser, { passive: true }));
      el._nsAnim = { cancel: () => finish(false) };

      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        el.scrollTo({ left: from + distance * easeOut(t), behavior: 'instant' });
        if (t < 1) frame = requestAnimationFrame(step);
        else finish(true);
      };
      frame = requestAnimationFrame(step);
    });
  }

  // Alle Slider messen gesammelt: erst lesen alle, dann schreiben alle. Einzeln
  // (schreiben -> lesen je Slider) erzwang jeder Slider beim Start ein eigenes Layout.
  const pendingMeasures = new Set();
  function flushMeasures() {
    const list = Array.from(pendingMeasures);
    pendingMeasures.clear();
    const results = list.map((m) => m.read());
    list.forEach((m, i) => m.write(results[i]));
  }

  function NativeSlider(viewport, options, plugins) {
    options = options || {};
    const container = viewport.firstElementChild;
    const listeners = {};
    const cleanups = [];
    let snaps = [0];
    let max = 0;
    let measured = false;
    let selected = 0;
    let settleTimer;
    let scrollTicking = false;
    let destroyed = false;

    function emit(event) {
      (listeners[event] || []).slice().forEach((fn) => fn(api, event));
    }

    function listen(target, event, fn, opts) {
      target.addEventListener(event, fn, opts);
      cleanups.push(() => target.removeEventListener(event, fn, opts));
    }

    function slides() {
      return container ? Array.from(container.children) : [];
    }


    // Einrastpunkte wie Embla mit containScroll: 'trimSnaps': Slide-Anfaenge (bzw.
    // -Mitten bei align: 'center'), auf den scrollbaren Bereich begrenzt, dedupliziert.
    // Wie Embla relativ zum ersten Slide: dessen Abstand zum Viewport-Anfang (Padding
    // von Viewport oder Container, Spacer, Margin) wird zum scroll-padding, damit der
    // Startpunkt 0 ein Einrastpunkt ist und der Browser beim Laden nicht verschiebt.
    // read() liest nur, write() schreibt nur (siehe flushMeasures).
    const measurer = {
      read() {
        const center = options.align === 'center';
        const vpRect = viewport.getBoundingClientRect();
        const scrollLeft = viewport.scrollLeft;
        const width = viewport.clientWidth;
        const maxScroll = Math.max(0, viewport.scrollWidth - width);
        const items = slides();
        const padStart = center || !items[0] ? 0
          : Math.max(0, items[0].getBoundingClientRect().left - vpRect.left + scrollLeft);
        const points = [];
        items.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          const left = center
            ? rect.left + rect.width / 2 - vpRect.left + scrollLeft - width / 2
            : rect.left - vpRect.left + scrollLeft - padStart;
          const pos = Math.min(maxScroll, Math.max(0, Math.round(left)));
          if (!points.length || Math.abs(pos - points[points.length - 1]) > 1) points.push(pos);
        });
        return { padding: center ? '' : padStart + 'px', points, maxScroll, scrollLeft };
      },
      write(r) {
        if (viewport.style.scrollPaddingLeft !== r.padding) viewport.style.scrollPaddingLeft = r.padding;
        snaps = r.points.length ? r.points : [0];
        max = r.maxScroll;
        measured = true;
        selected = Math.min(closestSnap(r.scrollLeft), snaps.length - 1);
      },
    };

    function invalidate() {
      measured = false;
      pendingMeasures.add(measurer);
    }

    function ensureMeasured() {
      if (!measured) {
        pendingMeasures.add(measurer);
        flushMeasures();
      }
    }

    function closestSnap(x) {
      if (x === undefined) {
        ensureMeasured();
        x = viewport.scrollLeft;
      }
      let best = 0;
      snaps.forEach((pos, i) => {
        if (Math.abs(pos - x) < Math.abs(snaps[best] - x)) best = i;
      });
      return best;
    }

    function onScroll() {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        scrollTicking = false;
        if (destroyed) return;
        emit('scroll');
        const index = closestSnap();
        if (index !== selected) {
          selected = index;
          emit('select');
        }
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => emit('settle'), 150);
      });
    }

    function scrollTo(index, jump) {
      ensureMeasured();
      if (options.loop) index = (index + snaps.length) % snaps.length;
      index = Math.max(0, Math.min(index, snaps.length - 1));
      if (jump) {
        if (viewport._nsAnim) viewport._nsAnim.cancel();
        viewport.scrollTo({ left: snaps[index], behavior: 'instant' });
      } else {
        animateTo(viewport, snaps[index]).then(() => { if (!destroyed) emit('settle'); });
      }
      if (index !== selected) {
        selected = index;
        emit('select');
      }
    }

    // Maus-Drag am Desktop (Touch und Trackpad scrollen nativ). Beim Loslassen
    // traegt der Schwung weiter - schnelles Wischen kann mehrere Slides ueberspringen.
    function initMouseDrag() {
      let startX = 0, startScroll = 0, dragging = false, moved = false, samples = [];
      listen(viewport, 'pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        ensureMeasured();
        if (snaps.length < 2) return;
        if (viewport._nsAnim) viewport._nsAnim.cancel();
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = viewport.scrollLeft;
        samples = [{ x: e.clientX, t: e.timeStamp }];
      });
      listen(window, 'pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 3) {
          moved = true;
          viewport.classList.add('is-dragging');
        }
        if (moved) viewport.scrollTo({ left: startScroll - dx, behavior: 'instant' });
        samples.push({ x: e.clientX, t: e.timeStamp });
        if (samples.length > 6) samples.shift();
      }, { passive: true });
      listen(window, 'pointerup', (e) => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        const dx = e.clientX - startX;
        // Geschwindigkeit ueber die letzten ~100 ms (px/ms, positiv = nach rechts gezogen)
        const recent = samples.filter((p) => e.timeStamp - p.t < 100);
        const first = recent[0] || samples[0];
        const velocity = e.timeStamp > first.t ? (e.clientX - first.x) / (e.timeStamp - first.t) : 0;
        const projected = viewport.scrollLeft - velocity * MOMENTUM;
        let target = closestSnap(projected);
        const startIndex = closestSnap(startScroll);
        // Bewusster Zug ueber die Schwelle bewegt mindestens um einen Slide
        if (target === startIndex) {
          if (dx < -DRAG_THRESHOLD) target = startIndex + 1;
          else if (dx > DRAG_THRESHOLD) target = startIndex - 1;
        }
        // is-dragging erst entfernen, wenn die Animation das Einrasten uebernimmt
        scrollTo(target);
        viewport.classList.remove('is-dragging');
      });
      // Klick nach einem Drag nicht als Link-Klick werten
      listen(viewport, 'click', (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      }, true);
      listen(viewport, 'dragstart', (e) => e.preventDefault());
    }

    const api = {
      on(event, fn) {
        (listeners[event] = listeners[event] || []).push(fn);
        return api;
      },
      off(event, fn) {
        listeners[event] = (listeners[event] || []).filter((f) => f !== fn);
        return api;
      },
      scrollNext(jump) { scrollTo(selected + 1, jump); },
      scrollPrev(jump) { scrollTo(selected - 1, jump); },
      scrollTo(index, jump) { scrollTo(index, jump); },
      canScrollNext() { ensureMeasured(); return !!options.loop && snaps.length > 1 || selected < snaps.length - 1; },
      canScrollPrev() { ensureMeasured(); return !!options.loop && snaps.length > 1 || selected > 0; },
      scrollSnapList() { ensureMeasured(); return snaps.map((pos) => (max ? pos / max : 0)); },
      selectedScrollSnap() { ensureMeasured(); return selected; },
      slideNodes() { return slides(); },
      rootNode() { return viewport; },
      containerNode() { return container; },
      reInit() {
        if (viewport._nsAnim) return;
        invalidate();
        ensureMeasured();
        emit('reInit');
      },
      destroy() {
        destroyed = true;
        pendingMeasures.delete(measurer);
        cleanups.forEach((fn) => fn());
        (api._plugins || []).forEach((p) => p.destroy && p.destroy());
        viewport.classList.remove('ns-viewport', 'ns-viewport--center', 'is-dragging');
        viewport.style.scrollPaddingLeft = '';
        emit('destroy');
      },
    };

    if (!container) return api;

    viewport.classList.add('ns-viewport');
    viewport.classList.toggle('ns-viewport--center', options.align === 'center');
    // Messung erst beim ersten Bedarf, dann fuer alle wartenden Slider gemeinsam
    invalidate();
    listen(viewport, 'scroll', onScroll, { passive: true });
    initMouseDrag();

    // Neu messen, wenn sich die Groesse aendert (Breakpoints, Bilder, Fonts)
    if ('ResizeObserver' in window) {
      let resizeTimer;
      // Die erste Meldung je Element kommt direkt nach observe() - ohne Groessenaenderung
      const seen = new WeakSet();
      const ro = new ResizeObserver((entries) => {
        let changed = false;
        entries.forEach((entry) => {
          if (seen.has(entry.target)) changed = true;
          else seen.add(entry.target);
        });
        if (!changed) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => { if (!destroyed) api.reInit(); }, 100);
      });
      ro.observe(viewport);
      ro.observe(container);
      cleanups.push(() => ro.disconnect());
    }

    api._plugins = (plugins || []).filter(Boolean);
    api._plugins.forEach((p) => p.init && p.init(api, viewport, listen));

    // Wie Embla v8: init asynchron, damit danach registrierte Listener greifen
    setTimeout(() => { if (!destroyed) emit('init'); }, 0);
    return api;
  }

  /**
   * Autoplay wie EmblaCarouselAutoplay: { delay, stopOnInteraction, stopOnMouseEnter }.
   * Laeuft nur, solange der Slider sichtbar und der Tab aktiv ist.
   */
  function NativeSliderAutoplay(opts) {
    opts = Object.assign({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: false }, opts);
    let timer = null, stopped = false, visible = true, hovered = false, slider = null, io = null;

    function schedule() {
      clearTimeout(timer);
      if (stopped || !visible || hovered || document.hidden) return;
      timer = setTimeout(() => {
        if (slider.canScrollNext()) slider.scrollNext();
        else slider.scrollTo(0);
        schedule();
      }, opts.delay);
    }

    return {
      name: 'autoplay',
      init(api, viewport, listen) {
        slider = api;
        const stop = () => { if (opts.stopOnInteraction) { stopped = true; clearTimeout(timer); } };
        listen(viewport, 'pointerdown', stop, { passive: true });
        listen(viewport, 'wheel', stop, { passive: true });
        if (opts.stopOnMouseEnter) {
          listen(viewport, 'mouseenter', () => { hovered = true; clearTimeout(timer); });
          listen(viewport, 'mouseleave', () => { hovered = false; schedule(); });
        }
        listen(document, 'visibilitychange', schedule);
        if ('IntersectionObserver' in window) {
          io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; schedule(); });
          io.observe(viewport);
        }
        schedule();
      },
      destroy() {
        clearTimeout(timer);
        if (io) io.disconnect();
      },
    };
  }

  NativeSlider.animateTo = animateTo;
  window.NativeSlider = NativeSlider;
  window.NativeSliderAutoplay = NativeSliderAutoplay;
})();
